import type {
  ITransparencyProjectRepository,
  TransparencyProjectQueryParams,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
  TransparencyProject,
  CreateTransparencyProjectDTO,
  UpdateTransparencyProjectDTO,
  TransparencyTimelineUpdate,
  CreateTimelineUpdateDTO,
  TransparencyComment,
  TransparencyCommentType,
  CreateTransparencyCommentDTO,
  TransparencyProjectImage,
  TransparencyProjectDocument,
  AddProjectImageDTO,
  AddProjectDocumentDTO,
  TransparencyDashboardStats,
} from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

export class ApiTransparencyProjectRepository implements ITransparencyProjectRepository {
  private buildQuery(params?: TransparencyProjectQueryParams): string {
    return buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      category: params?.category,
      status: params?.status,
      priority: params?.priority,
      view: params?.view,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    });
  }

  async getProjects(
    params?: TransparencyProjectQueryParams
  ): Promise<PaginatedResponse<TransparencyProject>> {
    const client = getApiClient();
    return client.get<PaginatedResponse<TransparencyProject>>(
      `v1/transparency-projects?${this.buildQuery(params)}`
    );
  }

  async getPublishedProjects(
    params?: TransparencyProjectQueryParams
  ): Promise<PaginatedResponse<TransparencyProject>> {
    const client = getApiClient();
    return client.get<PaginatedResponse<TransparencyProject>>(
      `v1/transparency-projects/published?${this.buildQuery(params)}`
    );
  }

  async getHistoryProjects(
    params?: TransparencyProjectQueryParams
  ): Promise<PaginatedResponse<TransparencyProject>> {
    const client = getApiClient();
    return client.get<PaginatedResponse<TransparencyProject>>(
      `v1/transparency-projects/history?${this.buildQuery(params)}`
    );
  }

  async getProjectById(id: string): Promise<TransparencyProject | null> {
    const client = getApiClient();
    try {
      return await client.get<TransparencyProject>(`v1/transparency-projects/${id}`);
    } catch {
      return null;
    }
  }

  async createProject(data: CreateTransparencyProjectDTO): Promise<TransparencyProject> {
    const client = getApiClient();
    return client.post<TransparencyProject>('v1/transparency-projects', data);
  }

  async updateProject(id: string, data: UpdateTransparencyProjectDTO): Promise<TransparencyProject> {
    const client = getApiClient();
    return client.put<TransparencyProject>(`v1/transparency-projects/${id}`, data);
  }

  async publishProject(id: string): Promise<TransparencyProject> {
    const client = getApiClient();
    return client.patch<TransparencyProject>(`v1/transparency-projects/${id}/publish`);
  }

  async notifyResidents(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/transparency-projects/${id}/notify`);
  }

  async archiveProject(id: string): Promise<TransparencyProject> {
    const client = getApiClient();
    return client.patch<TransparencyProject>(`v1/transparency-projects/${id}/archive`);
  }

  async deleteProject(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${id}`);
  }

  async getTimeline(projectId: string): Promise<TransparencyTimelineUpdate[]> {
    const client = getApiClient();
    return client.get<TransparencyTimelineUpdate[]>(`v1/transparency-projects/${projectId}/timeline`);
  }

  async addTimelineUpdate(
    projectId: string,
    data: CreateTimelineUpdateDTO
  ): Promise<TransparencyTimelineUpdate> {
    const client = getApiClient();
    return client.post<TransparencyTimelineUpdate>(
      `v1/transparency-projects/${projectId}/timeline`,
      data
    );
  }

  async getComments(projectId: string, type?: TransparencyCommentType): Promise<TransparencyComment[]> {
    const client = getApiClient();
    const query = type ? `?type=${type}` : '';
    return client.get<TransparencyComment[]>(`v1/transparency-projects/${projectId}/comments${query}`);
  }

  async addComment(
    projectId: string,
    data: CreateTransparencyCommentDTO
  ): Promise<TransparencyComment> {
    const client = getApiClient();
    return client.post<TransparencyComment>(`v1/transparency-projects/${projectId}/comments`, data);
  }

  async deleteComment(projectId: string, commentId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${projectId}/comments/${commentId}`);
  }

  async getProjectImages(projectId: string): Promise<TransparencyProjectImage[]> {
    const client = getApiClient();
    return client.get<TransparencyProjectImage[]>(`v1/transparency-projects/${projectId}/images`);
  }

  async addProjectImage(
    projectId: string,
    data: AddProjectImageDTO
  ): Promise<TransparencyProjectImage> {
    const client = getApiClient();
    return client.post<TransparencyProjectImage>(
      `v1/transparency-projects/${projectId}/images`,
      data
    );
  }

  async deleteProjectImage(projectId: string, imageId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${projectId}/images/${imageId}`);
  }

  async getProjectDocuments(projectId: string): Promise<TransparencyProjectDocument[]> {
    const client = getApiClient();
    return client.get<TransparencyProjectDocument[]>(
      `v1/transparency-projects/${projectId}/documents`
    );
  }

  async addProjectDocument(
    projectId: string,
    data: AddProjectDocumentDTO
  ): Promise<TransparencyProjectDocument> {
    const client = getApiClient();
    return client.post<TransparencyProjectDocument>(
      `v1/transparency-projects/${projectId}/documents`,
      data
    );
  }

  async deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${projectId}/documents/${documentId}`);
  }

  async getLinks(projectId: string): Promise<{ announcementIds: string[]; pollIds: string[] }> {
    const client = getApiClient();
    return client.get(`v1/transparency-projects/${projectId}/links`);
  }

  async linkAnnouncement(projectId: string, announcementId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/transparency-projects/${projectId}/announcements/${announcementId}`);
  }

  async unlinkAnnouncement(projectId: string, announcementId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${projectId}/announcements/${announcementId}`);
  }

  async linkSurvey(projectId: string, pollId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/transparency-projects/${projectId}/surveys/${pollId}`);
  }

  async unlinkSurvey(projectId: string, pollId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/transparency-projects/${projectId}/surveys/${pollId}`);
  }

  async getDashboardStats(): Promise<TransparencyDashboardStats> {
    const client = getApiClient();
    return client.get<TransparencyDashboardStats>('v1/transparency-projects/dashboard/stats');
  }
}
